import {cva, type VariantProps} from 'platform/shared-tw';

export type {DiagramVariant} from '../../CarDiagram.types';

/**
 * Fill color CVA for SVG car parts.
 * Uses [pointer-events:fill] so only filled areas are interactive.
 * Hover uses `group-hover:` so all shapes of a multi-path part recolor as one;
 * the SVG root must have the `group` class.
 */
export const partFill = cva('[pointer-events:fill]', {
  variants: {
    variant: {
      default: 'text-(--color-car-part) group-hover:text-(--color-car-selection)',
      damage: 'text-(--color-car-part-damage) group-hover:text-(--color-car-selection-damage)',
      service: 'text-(--color-car-part-service) group-hover:text-(--color-car-selection-service)',
      suggestions:
        'text-(--color-car-part-suggestions) group-hover:text-(--color-car-selection-suggestions)',
    },
  },
  defaultVariants: {variant: 'default'},
});

/**
 * Stroke color CVA for SVG car parts.
 * [pointer-events:none] so strokes don't intercept mouse events.
 */
export const partStroke = cva('[pointer-events:none]', {
  variants: {
    variant: {
      default: 'text-(--color-car-stroke)',
      damage: 'text-(--color-car-stroke-damage)',
      service: 'text-(--color-car-stroke-service)',
      suggestions: 'text-(--color-car-stroke-suggestions)',
    },
  },
  defaultVariants: {variant: 'default'},
});

/**
 * Neutral fill — always uses the car floor color (for base/background SVG paths).
 */
export const baseFill = cva(
  'text-(--color-car-floor) [pointer-events:fill] hover:text-(--color-car-selection)'
);

/**
 * Neutral stroke — always uses the car stroke color.
 */
export const baseStroke = cva('text-(--color-car-stroke) [pointer-events:none]');

export type PartVariantProps = VariantProps<typeof partFill>;

/**
 * Creates a keyboard handler for SVG part elements that activates on Enter or Space,
 * matching the behaviour expected of elements with role="button".
 * Returns undefined when no onClick is provided so the onKeyDown prop can be omitted entirely.
 */
export function createKeyDownHandler(
  onClick: (() => void) | undefined
): ((event: {key: string; preventDefault: () => void}) => void) | undefined {
  if (!onClick) {
    return undefined;
  }
  return (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };
}
